/**
 * AdminUsersPage: full in-app user management. Per the updated product
 * brief, venue owners should never need to log into Supabase after
 * initial setup — inviting, disabling, removing, and promoting/demoting
 * coordinators all happen here now, backed by three small Edge Functions
 * (see supabase/functions/) that hold the privileged service_role key
 * server-side and verify the caller is an admin before doing anything.
 */

import { useState, type FormEvent } from 'react';
import {
  useCoordinatorsList,
  useInviteCoordinator,
  useManageCoordinator,
  useUpdateCoordinatorRole,
  type CoordinatorListEntry,
} from '../api/coordinators';
import { Toast, ToastStack } from '../components/shared/Toast';

type ConfirmAction = { entry: CoordinatorListEntry; action: 'disable' | 'enable' | 'remove' };

export function AdminUsersPage() {
  const { data: users, isLoading, error: listError } = useCoordinatorsList();
  const inviteCoordinator = useInviteCoordinator();
  const manageCoordinator = useManageCoordinator();
  const updateRole = useUpdateCoordinatorRole();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const [confirming, setConfirming] = useState<ConfirmAction | null>(null);

  async function handleInviteSubmit(e: FormEvent) {
    e.preventDefault();
    setInviteError(null);

    try {
      await inviteCoordinator.mutateAsync({ email: inviteEmail.trim(), fullName: inviteName.trim() });
      setToast({ message: `Invitation sent to ${inviteEmail.trim()}.`, tone: 'success' });
      setInviteOpen(false);
      setInviteName('');
      setInviteEmail('');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Something went wrong sending that invite.');
    }
  }

  async function handleConfirmedAction() {
    if (!confirming) return;
    const { entry, action } = confirming;
    setConfirming(null);

    try {
      await manageCoordinator.mutateAsync({ userId: entry.id, action });
      const verb = action === 'disable' ? 'disabled' : action === 'enable' ? 're-enabled' : 'removed';
      setToast({ message: `${entry.fullName || entry.email || 'That account'} was ${verb}.`, tone: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
        tone: 'error',
      });
    }
  }

  function handleRoleChange(entry: CoordinatorListEntry, role: 'admin' | 'coordinator') {
    updateRole.mutate(
      { id: entry.id, role },
      {
        onError: () =>
          setToast({ message: `Couldn\u2019t update ${entry.fullName || 'that user'}\u2019s role.`, tone: 'error' }),
      }
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl text-ink-700">Users</h1>
          <p className="mt-1 font-body text-sm text-ink-400">
            Invite, promote, disable, or remove coordinators \u2014 all from here. No need to log
            into Supabase.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="shrink-0 rounded-lg bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
        >
          + Invite Coordinator
        </button>
      </div>

      {isLoading && <p className="mt-6 font-body text-sm text-ink-400">{'Loading\u2026'}</p>}

      {listError && (
        <p className="mt-6 rounded border border-danger/30 bg-danger/5 px-3 py-2 font-body text-sm text-danger">
          {listError instanceof Error ? listError.message : 'Could not load the user list.'}
        </p>
      )}

      <div className="mt-6 divide-y divide-ivory-200 rounded-lg border border-ivory-300 bg-white">
        {users?.map((entry) => (
          <div key={entry.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate font-body text-sm font-medium text-ink-700">
                  {entry.fullName || entry.email || 'Unnamed user'}
                </p>
                {entry.isDisabled && (
                  <span className="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 font-body text-xs font-medium text-danger">
                    Disabled
                  </span>
                )}
              </div>
              <p className="truncate font-body text-xs text-ink-400">
                {entry.email ?? 'No email on file'} {'\u00b7'} Joined{' '}
                {new Date(entry.createdAt).toLocaleDateString()}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={entry.role}
                onChange={(e) => handleRoleChange(entry, e.target.value as 'admin' | 'coordinator')}
                className="rounded border border-ivory-300 bg-white px-2 py-1.5 font-body text-sm text-ink-700"
              >
                <option value="coordinator">Coordinator</option>
                <option value="admin">Admin</option>
              </select>

              {entry.isDisabled ? (
                <button
                  onClick={() => setConfirming({ entry, action: 'enable' })}
                  className="rounded border border-evergreen-300 px-3 py-1.5 font-body text-sm text-evergreen-700 hover:bg-evergreen-50"
                >
                  Re-enable
                </button>
              ) : (
                <button
                  onClick={() => setConfirming({ entry, action: 'disable' })}
                  className="rounded border border-ivory-300 px-3 py-1.5 font-body text-sm text-ink-600 hover:bg-ivory-100"
                >
                  Disable
                </button>
              )}

              <button
                onClick={() => setConfirming({ entry, action: 'remove' })}
                className="rounded border border-danger/40 px-3 py-1.5 font-body text-sm text-danger hover:bg-danger/5"
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {!isLoading && users?.length === 0 && (
          <p className="px-4 py-6 text-center font-body text-sm text-ink-400">No users yet.</p>
        )}
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-ivory-50 p-6 shadow-elevated">
            <h2 className="font-display text-xl text-ink-700">Invite a coordinator</h2>
            <p className="mt-1 font-body text-sm text-ink-400">
              They&rsquo;ll get an email with a link to set up their account. They&rsquo;ll start
              as a Coordinator \u2014 you can promote them to Admin afterward if needed.
            </p>

            <form onSubmit={handleInviteSubmit} className="mt-5 space-y-4">
              <label className="block">
                <span className="font-body text-sm font-medium text-ink-600">Name</span>
                <input
                  type="text"
                  required
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Jane Smith"
                  className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
                />
              </label>

              <label className="block">
                <span className="font-body text-sm font-medium text-ink-600">Email address</span>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
                />
              </label>

              {inviteError && <p className="font-body text-sm text-danger">{inviteError}</p>}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setInviteOpen(false);
                    setInviteError(null);
                  }}
                  className="rounded px-4 py-2 font-body text-sm text-ink-500 hover:bg-ivory-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteCoordinator.isPending}
                  className="rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700 disabled:opacity-60"
                >
                  {inviteCoordinator.isPending ? 'Sending\u2026' : 'Send invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation modal — every destructive/account-affecting action
          requires an explicit confirmation, never a single click. */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-ivory-50 p-6 shadow-elevated">
            <h2 className="font-display text-lg text-ink-700">
              {confirming.action === 'remove'
                ? 'Remove this account?'
                : confirming.action === 'disable'
                  ? 'Disable this account?'
                  : 'Re-enable this account?'}
            </h2>
            <p className="mt-2 font-body text-sm text-ink-500">
              {confirming.action === 'remove' && (
                <>
                  This permanently deletes {confirming.entry.fullName || confirming.entry.email}&rsquo;s
                  account. This can&rsquo;t be undone.
                </>
              )}
              {confirming.action === 'disable' && (
                <>
                  {confirming.entry.fullName || confirming.entry.email} won&rsquo;t be able to sign in
                  until you re-enable their account. Nothing is deleted.
                </>
              )}
              {confirming.action === 'enable' && (
                <>{confirming.entry.fullName || confirming.entry.email} will be able to sign in again.</>
              )}
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirming(null)}
                className="rounded px-4 py-2 font-body text-sm text-ink-500 hover:bg-ivory-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedAction}
                className={
                  confirming.action === 'remove'
                    ? 'rounded bg-danger px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-danger/90'
                    : 'rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700'
                }
              >
                {confirming.action === 'remove'
                  ? 'Remove account'
                  : confirming.action === 'disable'
                    ? 'Disable account'
                    : 'Re-enable account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastStack>
        {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
      </ToastStack>
    </div>
  );
}
